import { useEffect, useState, useCallback, useMemo } from "react";
import { useTeams } from "../lib/teams";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  ViewModeToggle,
  SortSelect,
  useViewMode,
  useSortKey,
  sortItems,
} from "../components/ui/view-helpers";
import { Plus, Search, Pencil, Trash2, Star } from "lucide-react";
import { toast } from "sonner";
import PlayerCard from "../components/players/PlayerCard";
import PlayerFormDialog from "../components/players/PlayerFormDialog";
import { emptyPlayer } from "../components/players/playerHelpers";

const ROLE_ORDER = ["Portiere", "Difensore", "Centrocampista", "Attaccante"];
const ROLE_META = {
  Portiere: { color: "bg-amber-100 text-amber-700 border-amber-200", icon: "🧤" },
  Difensore: { color: "bg-blue-100 text-blue-700 border-blue-200", icon: "🛡️" },
  Centrocampista: { color: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: "⚙️" },
  Attaccante: { color: "bg-red-100 text-red-700 border-red-200", icon: "⚽" },
};
const SORT_OPTIONS = [
  { value: "name", label: "Alfabetico" },
  { value: "jersey", label: "Numero maglia" },
  { value: "role", label: "Ruolo" },
  { value: "birth_date", label: "Data di nascita" },
];

export default function PlayersPage() {
  const { activeTeam, canEdit, season } = useTeams();
  const [players, setPlayers] = useState([]);
  const [ratingSummary, setRatingSummary] = useState({}); // player_id -> {average, count}
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyPlayer);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [viewMode, changeView] = useViewMode("playersViewMode");
  const [sortKey, setSortKey] = useSortKey("playersSortKey", "name");

  const load = useCallback(async () => {
    if (!activeTeam) return;
    const [pRes, rRes] = await Promise.all([
      api.get(`/teams/${activeTeam.id}/players`),
      api
        .get(`/teams/${activeTeam.id}/ratings/summary`, { params: { season } })
        .catch(() => ({ data: [] })),
    ]);
    setPlayers(pRes.data);
    const map = {};
    (rRes.data || []).forEach((r) => {
      map[r.player_id] = { average: r.average, count: r.count };
    });
    setRatingSummary(map);
  }, [activeTeam, season]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const matched = players.filter((p) =>
      `${p.first_name} ${p.last_name} ${p.role || ""}`
        .toLowerCase()
        .includes(search.toLowerCase())
    );
    return sortItems(matched, sortKey);
  }, [players, search, sortKey]);

  const grouped = useMemo(() => {
    const buckets = {};
    for (const p of filtered) {
      const r = ROLE_ORDER.includes(p.role) ? p.role : "Senza ruolo";
      (buckets[r] = buckets[r] || []).push(p);
    }
    // stable sort inside each bucket: jersey_number asc then last_name asc
    const collator = new Intl.Collator("it", { sensitivity: "base" });
    Object.values(buckets).forEach((arr) =>
      arr.sort((a, b) => {
        const an = Number(a.jersey_number) || 9999;
        const bn = Number(b.jersey_number) || 9999;
        if (an !== bn) return an - bn;
        return collator.compare(a.last_name || "", b.last_name || "");
      })
    );
    const order = [...ROLE_ORDER, "Senza ruolo"];
    return order
      .filter((k) => buckets[k]?.length)
      .map((k) => ({ role: k, players: buckets[k] }));
  }, [filtered]);

  if (!activeTeam) return <p className="text-slate-500">Seleziona una squadra.</p>;

  const submit = async () => {
    if (!form.first_name || !form.last_name) {
      toast.error("Nome e cognome richiesti");
      return;
    }
    const payload = {
      ...form,
      team_id: activeTeam.id,
      jersey_number: form.jersey_number ? Number(form.jersey_number) : null,
    };
    if (editingId) await api.put(`/players/${editingId}`, payload);
    else await api.post(`/players`, payload);
    toast.success(editingId ? "Giocatore aggiornato" : "Giocatore aggiunto");
    setOpen(false);
    setForm(emptyPlayer);
    setEditingId(null);
    load();
  };

  const handleEdit = (p) => {
    setForm({ ...emptyPlayer, ...p, jersey_number: p.jersey_number ?? "" });
    setEditingId(p.id);
    setOpen(true);
  };

  const handleNew = () => {
    setForm(emptyPlayer);
    setEditingId(null);
    setOpen(true);
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Eliminare ${p.first_name} ${p.last_name}?`)) return;
    await api.delete(`/players/${p.id}`);
    toast.success("Giocatore rimosso");
    load();
  };

  return (
    <div className="space-y-6" data-testid="players-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Anagrafica</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Giocatori</h1>
          <p className="text-slate-500">
            {filtered.length} {filtered.length === 1 ? "giocatore" : "giocatori"} in rosa
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <ViewModeToggle
            value={viewMode}
            onChange={changeView}
            testid="players-view-toggle"
          />
          <SortSelect
            value={sortKey}
            onChange={setSortKey}
            options={SORT_OPTIONS}
            testid="players-sort"
          />
          <div className="relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <Input
              placeholder="Cerca..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 w-56"
              data-testid="players-search"
            />
          </div>
          <Button
            onClick={handleNew}
            className="rounded-full bg-sky-500 hover:bg-sky-600"
            data-testid="new-player-btn"
            disabled={!canEdit}
            style={!canEdit ? { display: "none" } : {}}
          >
            <Plus size={16} className="mr-1" /> Nuovo
          </Button>
        </div>
      </div>

      {viewMode === "cards" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((p) => (
            <PlayerCard
              key={p.id}
              player={p}
              canEdit={canEdit}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))}
          {filtered.length === 0 && (
            <Card className="p-10 col-span-full text-center text-slate-500 border-dashed">
              Nessun giocatore. Aggiungi il primo!
            </Card>
          )}
        </div>
      )}

      {viewMode === "list" && (
        <Card className="overflow-hidden" data-testid="players-list-view">
          {filtered.length === 0 ? (
            <div className="p-10 text-center text-slate-500">
              Nessun giocatore.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-4 py-3 text-center w-14">#</th>
                    <th className="px-4 py-3 text-left">Giocatore</th>
                    <th className="px-4 py-3 text-left">Ruolo</th>
                    <th className="px-4 py-3 text-left">Voto medio</th>
                    <th className="px-4 py-3 text-left">Data nascita</th>
                    <th className="px-4 py-3 text-left">Codice Fiscale</th>
                    {canEdit && <th className="px-4 py-3 text-right">Azioni</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((p) => {
                    const meta = ROLE_META[p.role];
                    return (
                      <tr
                        key={p.id}
                        className="hover:bg-slate-50 transition"
                        data-testid={`player-row-${p.id}`}
                      >
                        <td className="px-4 py-3 text-center font-mono font-bold text-slate-700">
                          {p.jersey_number ?? "—"}
                        </td>
                        <td className="px-4 py-3 font-medium">
                          {p.last_name} {p.first_name}
                        </td>
                        <td className="px-4 py-3">
                          {p.role ? (
                            <Badge
                              variant="outline"
                              className={`font-medium ${meta?.color || ""}`}
                            >
                              {meta?.icon} {p.role}
                            </Badge>
                          ) : (
                            <span className="text-slate-400 text-xs">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {ratingSummary[p.id] ? (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold"
                              title={`${ratingSummary[p.id].count} valutazioni`}
                              data-testid={`player-avg-${p.id}`}
                            >
                              <Star size={11} fill="currentColor" />
                              {ratingSummary[p.id].average}
                              <span className="text-amber-500/70 font-normal">
                                ({ratingSummary[p.id].count})
                              </span>
                            </span>
                          ) : (
                            <span className="text-slate-300 text-xs">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {p.birth_date || "—"}
                        </td>
                        <td className="px-4 py-3 text-slate-500 font-mono text-xs">
                          {p.fiscal_code || "—"}
                        </td>
                        {canEdit && (
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEdit(p)}
                              data-testid={`edit-player-${p.id}`}
                            >
                              <Pencil size={14} />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(p)}
                              data-testid={`delete-player-${p.id}`}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {viewMode === "group" && (
        <div className="space-y-6" data-testid="players-role-view">
          {grouped.length === 0 && (
            <Card className="p-10 text-center text-slate-500 border-dashed">
              Nessun giocatore.
            </Card>
          )}
          {grouped.map(({ role, players: list }) => {
            const meta = ROLE_META[role] || {
              color: "bg-slate-100 text-slate-600 border-slate-200",
              icon: "👤",
            };
            return (
              <div key={role}>
                <div className="flex items-center gap-2 mb-3">
                  <Badge
                    variant="outline"
                    className={`text-sm font-bold ${meta.color}`}
                  >
                    {meta.icon} {role}
                  </Badge>
                  <span className="text-xs text-slate-400">
                    {list.length} {list.length === 1 ? "giocatore" : "giocatori"}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {list.map((p) => (
                    <PlayerCard
                      key={p.id}
                      player={p}
                      canEdit={canEdit}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <PlayerFormDialog
        open={open}
        onOpenChange={setOpen}
        form={form}
        setForm={setForm}
        editingId={editingId}
        onSubmit={submit}
      />
    </div>
  );
}
